import type { LocationQuery, RouteLocationRaw } from 'vue-router'

import { parse as parseHash } from '@backmarket/utils/query-string/parse'
import { stringify as stringifyHash } from '@backmarket/utils/query-string/stringify'

// Pagination and search query, plus third-party tracking/advertising params.
// These must NOT trigger a redirect: a redirect on a URL containing unknown
// params would create a redirect loop when the same params are re-appended by
// ad platforms or browsers after the 301, leading to "too many redirects".
// These params are also stripped from the Cloudflare cache key to prevent
// cache poisoning — see https://github.com/BackMarket/openworld/blob/d9322c99ecd5c0f7b876ced86b891d9d9adf6923/global/prod/cloudflare-cache-rules/cloudflare.hcl#L30
const ALWAYS_ALLOWED_QUERY_KEYS = [
  'p',
  'q',
  '_gl',
  'af_channel',
  'af_referrer_customer_id',
  'af_referrer_uid',
  'af_siteid',
  'awc',
  'fbclid',
  'gad_campaignid',
  'gad_source',
  'gclid',
  'gbraid',
  'gclsrc',
  'msclkid',
  'sc_lid',
  'sc_llid',
  'sc_src',
  'sc_uid',
  'shopping',
  'srsltid',
  'sv_campaign_id',
  'sv1',
  'ttclid',
  'utm_campaign',
  'utm_content',
  'utm_id',
  'utm_medium',
  'utm_override',
  'utm_source',
  'utm_term',
  'wbraid',
]
const MIN_PRODUCTS_FOR_FILTERED_PAGE = 2

type RouteContext = {
  path: string
  query: LocationQuery
  hash: string
}

export function getUnknownQueryKeyRedirect(
  { path, query, hash }: RouteContext,
  openFilterKey: string | null,
  hasOpenFacetsError: boolean,
): RouteLocationRaw | null {
  if (hasOpenFacetsError) return null

  const allowedKeys = new Set([
    ...(openFilterKey ? [openFilterKey] : []),
    ...ALWAYS_ALLOWED_QUERY_KEYS,
  ])
  const hasUnknownKey = Object.keys(query).some((key) => !allowedKeys.has(key))
  if (!hasUnknownKey) return null

  const cleanQuery: LocationQuery = {}
  for (const key of ALWAYS_ALLOWED_QUERY_KEYS) {
    if (query[key] != null) cleanQuery[key] = query[key]
  }

  return { path, query: cleanQuery, hash }
}

export function getOpenFilterHashRedirect(
  { path, query, hash }: RouteContext,
  openFilterKey: string | null,
): RouteLocationRaw | null {
  if (!openFilterKey) return null

  const currentHashFilters = hash ? parseHash(hash.replace(/^#/, '')) : {}
  if (!(openFilterKey in currentHashFilters)) return null

  const { [openFilterKey]: _dropped, ...rest } = currentHashFilters
  const newHashBody = stringifyHash(rest)

  return { path, query, hash: newHashBody ? `#${newHashBody}` : '' }
}

export function getThinResultsRedirect(
  { path, query, hash }: RouteContext,
  openFilterKey: string | null,
  total: number,
): RouteLocationRaw | null {
  if (!openFilterKey || total >= MIN_PRODUCTS_FOR_FILTERED_PAGE) return null

  const { [openFilterKey]: _dropped, ...restQuery } = query

  return { path, query: restQuery, hash }
}
